import React, { useState } from 'react';
import FolderTreeItem from './FolderTreeItem';
import FolderDialog from './FolderDialog';
import { FolderPlusIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import './FolderSidebar.css';

function FolderSidebar({
  folderTree,
  currentFolderId,
  onFolderSelect,
  onCreateFolder,
  onUpdateFolder,
  onDeleteFolder,
  onMoveFolder,
  onDrop,
  isCollapsed,
  onToggleCollapse
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [createParentId, setCreateParentId] = useState(null);

  const handleCreateClick = (parentId = null) => {
    setCreateParentId(parentId);
    setEditingFolder(null);
    setIsDialogOpen(true);
  };

  const handleEditClick = (folder) => {
    setEditingFolder(folder);
    setCreateParentId(folder.parentId);
    setIsDialogOpen(true);
  };

  const handleDialogSave = async (name, color, icon) => {
    try {
      if (editingFolder) {
        await onUpdateFolder(editingFolder.id, { name, color, icon });
      } else {
        await onCreateFolder(name, createParentId, color, icon);
      }
      setIsDialogOpen(false);
      setEditingFolder(null);
      setCreateParentId(null);
    } catch (error) {
      // Error is handled in the dialog
      throw error;
    }
  };

  const handleDialogDelete = async (folderId) => {
    try {
      await onDeleteFolder(folderId);
      setIsDialogOpen(false);
      setEditingFolder(null);
      setCreateParentId(null);
    } catch (error) {
      throw error;
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingFolder(null);
    setCreateParentId(null);
  };

  const handleRootDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleRootDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleRootDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    onDrop(e, null);
  };

  if (isCollapsed) {
    return (
      <aside className="folder-sidebar collapsed">
        <button
          className="sidebar-toggle"
          onClick={onToggleCollapse}
          title="Expand sidebar"
        >
          <ChevronRightIcon className="icon" />
        </button>
      </aside>
    );
  }

  return (
    <aside className="folder-sidebar">
      <div className="folder-sidebar-header">
        <h3>Folders</h3>
        <div className="header-actions">
          <button
            className="create-folder-btn"
            onClick={() => handleCreateClick(null)}
            title="Create folder"
          >
            <FolderPlusIcon className="icon" />
          </button>
          <button
            className="sidebar-toggle"
            onClick={onToggleCollapse}
            title="Collapse sidebar"
          >
            <ChevronLeftIcon className="icon" />
          </button>
        </div>
      </div>

      <nav className="folder-tree">
        {/* All Transcripts (root) option */}
        <div
          className={`folder-tree-item root-item ${currentFolderId === null ? 'active' : ''}`}
          onClick={() => onFolderSelect(null)}
          onDragOver={handleRootDragOver}
          onDragLeave={handleRootDragLeave}
          onDrop={handleRootDrop}
        >
          <span className="folder-icon">📋</span>
          <span className="folder-name">All Transcripts</span>
        </div>

        {/* Folder tree */}
        {folderTree.map(folder => (
          <FolderTreeItem
            key={folder.id}
            folder={folder}
            level={0}
            currentFolderId={currentFolderId}
            onSelect={onFolderSelect}
            onCreateSubfolder={handleCreateClick}
            onEdit={handleEditClick}
            onDelete={onDeleteFolder}
            onDrop={onDrop}
            onMove={onMoveFolder}
          />
        ))}

        {folderTree.length === 0 && (
          <div className="empty-folders">
            <p>No folders yet</p>
            <button
              className="create-first-folder-btn"
              onClick={() => handleCreateClick(null)}
            >
              Create your first folder
            </button>
          </div>
        )}
      </nav>

      <FolderDialog
        isOpen={isDialogOpen}
        folder={editingFolder}
        onSave={handleDialogSave}
        onDelete={handleDialogDelete}
        onClose={handleDialogClose}
      />
    </aside>
  );
}

export default FolderSidebar;
